import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { Plus, Trash, PencilSimple, ArrowLeft, Flask } from "@phosphor-icons/react";

export default function AdminCalendar() {
  const [params, setParams] = useSearchParams();
  const filterTour = params.get("tour_id") || "";
  const [tours, setTours] = useState([]);
  const [instances, setInstances] = useState([]);
  const [editing, setEditing] = useState(null); // null or instance object
  const [form, setForm] = useState({ tour_id: "", starts_at: "", capacity: 12, price_override: "", notes: "" });

  const reload = () => Promise.all([
    api.get("/tours").then(r => setTours(r.data)),
    api.get(`/instances${filterTour ? `?tour_id=${filterTour}` : ""}`).then(r => setInstances(r.data)),
  ]);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filterTour]);

  const startNew = () => {
    setEditing("new");
    // Default new tour dates to tomorrow at 5:00 PM (local). Format for datetime-local input: YYYY-MM-DDTHH:MM
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(17, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const defaultStart = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T17:00`;
    setForm({ tour_id: filterTour || tours[0]?.id || "", starts_at: defaultStart, capacity: 12, price_override: "", notes: "" });
  };
  const startEdit = (i) => { setEditing(i); setForm({ tour_id: i.tour_id, starts_at: i.starts_at.slice(0,16), capacity: i.capacity, price_override: i.price_override ?? "", notes: i.notes || "" }); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      tour_id: form.tour_id,
      starts_at: new Date(form.starts_at).toISOString(),
      capacity: Number(form.capacity),
      price_override: form.price_override === "" ? null : Number(form.price_override),
      notes: form.notes,
    };
    if (editing === "new") await api.post("/instances", payload);
    else await api.patch(`/instances/${editing.id}`, payload);
    setEditing(null); reload();
  };
  const del = async (i) => { if (!window.confirm("Delete this scheduled tour?")) return; await api.delete(`/instances/${i.id}`); reload(); };
  const simulate = async (i) => {
    const guests = window.prompt(`Simulate a ${guestCountSuggestion(i)}-guest booking on this date?\n\nThis will:\n• Send REAL emails to your admin_notify_email + the customer email you enter below\n• Lock the date out of public inventory\n• Add a row to the booking list (marked as test)\n• NOT charge any card\n\nEnter guest count, or click Cancel:`, String(guestCountSuggestion(i)));
    if (!guests) return;
    const guest_count = Math.max(1, Math.min(Number(guests) || 2, i.capacity || 12));
    const customer_email = window.prompt("Where should the customer-side test email go?\n(use a non-mysteryserved.com address — Gmail, Yahoo, your phone — so you actually see it):", "") || "";
    try {
      const r = await api.post("/admin/bookings/simulate", {
        instance_id: i.id,
        customer_name: "Test Guest",
        customer_email: customer_email,
        guest_count,
      });
      window.open(r.data.booking_url, "_blank");
      alert(`Test booking created!\n\nBooking ID: ${r.data.booking_id}\nPublic page: opened in new tab\n\nEmails were sent to: admin_notify_email${customer_email ? " + " + customer_email : ""}\nThe instance is now locked.\nTo reset for another test: delete the booking from Admin → Bookings (it's tagged "is_test").`);
      reload();
    } catch (e) {
      alert(`Couldn't simulate: ${e?.response?.data?.detail || e.message}`);
    }
  };
  const guestCountSuggestion = (i) => Math.min(2, i.capacity || 2);
  const tName = (id) => tours.find(t => t.id === id)?.title || "—";
  const filteredTour = tours.find(t => t.id === filterTour);

  const setFilter = (id) => {
    if (id) params.set("tour_id", id); else params.delete("tour_id");
    setParams(params);
  };

  return (
    <div className="p-10 md:p-12" data-testid="admin-calendar">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          {filteredTour ? (
            <>
              <Link to="/admin/tours" className="inline-flex items-center gap-1 text-ms-stone hover:text-ms-earth text-xs label-eyebrow">
                <ArrowLeft size={12} /> All tours
              </Link>
              <h1 className="serif text-4xl text-ms-earth mt-2">Dates · <span className="text-ms-brass">{filteredTour.title}</span></h1>
              <p className="text-ms-stone text-sm mt-2">Each tour has its own separate schedule. You're viewing dates for this tour only.</p>
            </>
          ) : (
            <>
              <p className="label-eyebrow text-ms-stone">Schedule</p>
              <h1 className="serif text-4xl text-ms-earth mt-2">All tour dates</h1>
              <p className="text-ms-stone text-sm mt-2">Each tour keeps its own schedule. Use the filter or open a tour's "Manage dates" to focus on one.</p>
            </>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={filterTour}
            onChange={e => setFilter(e.target.value)}
            className="border border-ms-edge rounded-md px-3 py-2 text-sm bg-white"
            data-testid="schedule-tour-filter"
          >
            <option value="">All tours</option>
            {tours.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
          <button onClick={startNew} disabled={!tours.length} className="btn-primary disabled:opacity-50" data-testid="new-instance-btn"><Plus size={16} /> Add Tour Date</button>
        </div>
      </div>

      <div className="mt-8 bg-white border border-ms-edge rounded-2xl overflow-hidden">
        {instances.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-ms-earth text-sm">{filteredTour ? `No dates yet for ${filteredTour.title}.` : "No instances scheduled."}</p>
            <button onClick={startNew} className="mt-4 btn-primary"><Plus size={14} /> Add the first date</button>
          </div>
        ) :
          <table className="w-full text-sm">
            <thead className="bg-ms-cream border-b border-ms-edge">
              <tr><th className="text-left p-4 label-eyebrow text-xs">Tour</th><th className="text-left p-4 label-eyebrow text-xs">Date & time</th><th className="text-left p-4 label-eyebrow text-xs">Capacity</th><th className="text-left p-4 label-eyebrow text-xs">Status</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {instances.map(i => {
                const dt = new Date(i.starts_at);
                return (
                  <tr key={i.id} className="border-b border-ms-edge last:border-0 hover:bg-ms-cream/40">
                    <td className="p-4 text-ms-earth">{tName(i.tour_id)}</td>
                    <td className="p-4 text-ms-stone">{dt.toLocaleString(undefined, {month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit'})}</td>
                    <td className="p-4 text-ms-earth">{i.booked_count}/{i.capacity}</td>
                    <td className="p-4"><span className={i.status === "scheduled" ? "text-ms-success" : "text-ms-stone"}>{i.status}</span></td>
                    <td className="p-4 text-right space-x-2">
                      {i.is_active !== false && (
                        <button
                          onClick={() => simulate(i)}
                          className="text-[#7C5BA0] hover:text-ms-earth text-xs inline-flex items-center gap-1"
                          title="Run the full post-payment flow against this date without calling Stripe. Sends real emails to admin_notify_email and locks the date as if a real booking happened."
                          data-testid={`simulate-instance-${i.id}`}
                        >
                          <Flask size={14} weight="fill" />Simulate
                        </button>
                      )}
                      <button onClick={() => startEdit(i)} className="text-ms-stone hover:text-ms-earth text-xs inline-flex items-center gap-1" data-testid={`edit-instance-${i.id}`}><PencilSimple size={14} />Edit</button>
                      <button onClick={() => del(i)} className="text-ms-danger text-xs inline-flex items-center gap-1"><Trash size={14} />Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        }
        {instances.length > 0 && (
          <div className="border-t border-ms-edge p-5 flex items-center justify-between flex-wrap gap-3 bg-ms-cream/30">
            <p className="text-ms-stone text-xs">Each click adds a new date — existing dates are never overwritten.</p>
            <button onClick={startNew} className="btn-primary" data-testid="add-another-date-btn"><Plus size={14} /> Add another date</button>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6" onClick={() => setEditing(null)}>
          <form onClick={e=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl p-8 w-full max-w-lg space-y-4" data-testid="instance-modal">
            <h2 className="serif text-2xl text-ms-earth">{editing === "new" ? "Add tour date" : "Edit tour date"}</h2>
            <label className="block">
              <span className="label-eyebrow text-ms-stone text-xs">Tour</span>
              <select value={form.tour_id} onChange={e=>setForm({...form, tour_id:e.target.value})} className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm" data-testid="instance-tour">
                {tours.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="label-eyebrow text-ms-stone text-xs">Date & time</span>
              <input type="datetime-local" value={form.starts_at} onChange={e=>setForm({...form, starts_at:e.target.value})} required className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm" data-testid="instance-datetime" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="label-eyebrow text-ms-stone text-xs">Capacity</span><input type="number" value={form.capacity} onChange={e=>setForm({...form, capacity:e.target.value})} required className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm" data-testid="instance-capacity" /></label>
              <label className="block"><span className="label-eyebrow text-ms-stone text-xs">Price override ($)</span><input type="number" value={form.price_override} onChange={e=>setForm({...form, price_override:e.target.value})} placeholder="Use base price" className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm" /></label>
            </div>
            <label className="block"><span className="label-eyebrow text-ms-stone text-xs">Notes</span><input value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm" /></label>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="instance-save">{editing === "new" ? "Add date" : "Save changes"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
